import {
  type FirebaseApp,
  type FirebaseOptions,
  getApps,
  initializeApp,
} from 'firebase/app'
import {
  type Auth,
  connectAuthEmulator,
  type Dependencies,
  getAuth,
  initializeAuth,
  inMemoryPersistence,
  onAuthStateChanged,
  setPersistence,
} from 'firebase/auth'
import {
  connectFirestoreEmulator,
  type Firestore,
  getFirestore,
} from 'firebase/firestore'
import {
  connectFunctionsEmulator,
  type Functions,
  getFunctions,
} from 'firebase/functions'

import { isInIframe } from '../env/isInIframe'
import { useAuthStore } from './authStore'

export type FirebaseConfig = {
  options: FirebaseOptions
  auth?: Dependencies
  firestore: {
    databaseId: string
  }
  emulators: {
    enabled: boolean
    host?: string
    authPort?: number
    firestorePort?: number
    functionsPort?: number
  }
}

let appInstance: FirebaseApp | null = null
let authInstance: Auth | null = null
let dbInstance: Firestore | null = null
let functionsInstance: Functions | null = null

const defaultHost = 'localhost'
const defaultAuthPort = 9099
const defaultFirestorePort = 8080
const defaultFunctionsPort = 5001

// Embedded portal sessions (the playground iframe) must not share Firebase
// Auth state with regular portal tabs on the same origin. They use a
// separately-named app so the IndexedDB key is distinct, AND in-memory
// persistence so the impersonation session never touches disk at all.
const IMPERSONATION_APP_NAME = 'impersonation'

const initApp = (config: FirebaseConfig, appName?: string): FirebaseApp => {
  return appName
    ? initializeApp(config.options, appName)
    : initializeApp(config.options)
}

const initAuth = (app: FirebaseApp, config: FirebaseConfig): Auth => {
  if (config.auth) {
    return initializeAuth(app, config.auth)
  }
  return getAuth(app)
}

const getDb = (app: FirebaseApp, config: FirebaseConfig): Firestore => {
  if (config.emulators.enabled) {
    return getFirestore(app)
  }
  return getFirestore(app, config.firestore.databaseId)
}

const connectEmulators = (
  auth: Auth,
  db: Firestore,
  functions: Functions,
  config: FirebaseConfig
): void => {
  if (!config.emulators.enabled) {
    return
  }

  const host = config.emulators.host || defaultHost
  const authPort = config.emulators.authPort || defaultAuthPort
  const dbPort = config.emulators.firestorePort || defaultFirestorePort
  const functionsPort = config.emulators.functionsPort || defaultFunctionsPort

  connectAuthEmulator(auth, `http://${host}:${authPort}`, {
    disableWarnings: true,
  })

  connectFirestoreEmulator(db, host, dbPort)
  connectFunctionsEmulator(functions, host, functionsPort)
}

const addAuthListener = (auth: Auth): void => {
  onAuthStateChanged(auth, async user => {
    const store = useAuthStore.getState()
    store.setChanging(true)
    store.setError(null)
    if (user) {
      try {
        await user.getIdToken(true)
      } catch (error) {
        store.setError(error as Error)
      }
    }
    store.setUser(user)
    store.setChanging(false)
    store.setPending(false)
  })
}

// Init firebase app, auth and db.
// Handles expo fast refresh issue.
export const initFirebase = (config: FirebaseConfig): void => {
  const appName = isInIframe() ? IMPERSONATION_APP_NAME : undefined
  const lookupName = appName ?? '[DEFAULT]'
  const existing = getApps().find(a => a.name === lookupName)

  if (!existing) {
    appInstance = initApp(config, appName)
    authInstance = initAuth(appInstance, config)
    dbInstance = getDb(appInstance, config)
    functionsInstance = getFunctions(appInstance)
    if (appName) {
      // Force in-memory persistence so the impersonation session never lands
      // in IndexedDB. Without this, signing in inside the iframe would
      // overwrite the regular portal tab's session on the same origin.
      // Attach a catch so a rejection (rare; some browsers may refuse) does
      // not surface as an unhandled rejection.
      setPersistence(authInstance, inMemoryPersistence).catch(err => {
        // eslint-disable-next-line no-console
        console.warn('[firebase/runtime] setPersistence failed', err)
      })
    }
    connectEmulators(authInstance, dbInstance, functionsInstance, config)
    addAuthListener(authInstance)
  } else {
    appInstance = existing
    authInstance = getAuth(appInstance)
    dbInstance = getDb(appInstance, config)
    functionsInstance = getFunctions(appInstance)
  }
}

export const getAppInstance = (): FirebaseApp => {
  if (!appInstance) {
    throw new Error('Firebase app is not initialized')
  }
  return appInstance
}

export const getAuthInstance = (): Auth => {
  if (!authInstance) {
    throw new Error('Firebase auth is not initialized')
  }

  return authInstance
}

export const getDbInstance = (): Firestore => {
  if (!dbInstance) {
    throw new Error('Firebase db is not initialized')
  }

  return dbInstance
}

export const getFunctionsInstance = (): Functions => {
  if (!functionsInstance) {
    throw new Error('Firebase functions are not initialized')
  }

  return functionsInstance
}
